import {
  getCountryCallingCode,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js/min';
import type { FC } from 'react';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  PhoneInput,
} from '@squire/ui';

import { SUPPORTED_PHONE_COUNTRIES } from '@/constants/countries';

import type { PhoneFormFieldProps } from './types';

// Longest real national number worldwide is well under 15 digits (E.164's
// own ceiling); probing beyond that would loop forever for a country with no
// max.
const MAX_PROBE_LENGTH = 15;
const maxNationalLengthCache = new Map<CountryCode, number>();

// `validatePhoneNumberLength` is metadata-driven per country, so this never
// hardcodes a digit count the way a NANP-only "cap at 10" would.
const getMaxNationalLength = (country: CountryCode): number => {
  const cached = maxNationalLengthCache.get(country);
  if (cached !== undefined) return cached;

  let max = 0;
  for (let length = 1; length <= MAX_PROBE_LENGTH; length++) {
    if (validatePhoneNumberLength('0'.repeat(length), country) !== 'TOO_LONG') {
      max = length;
    }
  }
  maxNationalLengthCache.set(country, max);
  return max;
};

// `val` is either a masked national string (no country code yet) or the
// E.164 PhoneInput emits once AsYouType resolves a candidate number — strip
// the calling code in the latter case so both compare on the same basis.
const countNationalDigits = (val: string, country: CountryCode): number => {
  const digits = val.replace(/\D/g, '');
  if (!val.startsWith('+')) return digits.length;

  const callingCode = getCountryCallingCode(country);
  return Math.max(0, digits.length - callingCode.length);
};

const PhoneFormField: FC<PhoneFormFieldProps> = ({
  name,
  defaultCountry = 'US',
  testID,
  placeholder,
  accessibilityLabel,
  accessibilityHint,
  disabled = false,
  guardOverflow = false,
}) => {
  return (
    <FormField
      name={name}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => {
        const handleChangeText = (val: string) => {
          if (validatePhoneNumberLength(val, defaultCountry) === 'TOO_LONG') {
            return;
          }

          // For '+'-prefixed values, libphonenumber derives the country from
          // the number itself, so the top-level validatePhoneNumberLength
          // check above already blocks TOO_LONG correctly even when the
          // user's selected country (tracked internally by PhoneInput's own
          // dropdown) differs from defaultCountry. The digit-count guard
          // below assumes defaultCountry is the active country, which only
          // holds for local-format input — its real target is MaskedInput's
          // trunk-prefix renumbering, which only happens on local-format
          // input. Applying it to '+'-prefixed values as well double-guards
          // against the wrong country's calling code and can swallow a
          // legitimate keystroke (e.g. a GB number typed at a US-default shop).
          if (guardOverflow && !val.startsWith('+')) {
            // `value` is this render's field value — i.e. the number as it
            // stood before this keystroke — so this stays correct across
            // remounts with a prefilled defaultPhone, unlike a manually-tracked
            // ref that would reset to 0.
            const previousCount = countNationalDigits(value, defaultCountry);
            const nextCount = countNationalDigits(val, defaultCountry);
            const maxLength = getMaxNationalLength(defaultCountry);
            const wasAlreadyComplete = previousCount >= maxLength;

            if (nextCount > previousCount && wasAlreadyComplete) {
              // A keystroke landed past a complete number — MaskedInput let it
              // through the visible mask into AsYouType, which can renumber the
              // whole field around it. Drop it instead of letting a mangled
              // number reach the form.
              return;
            }
          }

          onChange(val);
        };

        return (
          <FormItem>
            <FormControl>
              <PhoneInput
                // PhoneInput reads defaultCountry only on mount, so a
                // late shop country needs a remount.
                key={defaultCountry}
                testID={testID}
                defaultCountry={defaultCountry}
                pinnedCountries={SUPPORTED_PHONE_COUNTRIES}
                showMaskPlaceholder
                placeholder={placeholder}
                value={value}
                onChangeText={handleChangeText}
                onBlur={onBlur}
                keyboardType="phone-pad"
                autoComplete="tel"
                error={!!error?.message}
                disabled={disabled}
                accessibilityLabel={accessibilityLabel}
                accessibilityHint={accessibilityHint}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

PhoneFormField.displayName = 'PhoneFormField';
export { PhoneFormField };
